import { useEffect, useRef, type KeyboardEvent } from 'react'
import { useDagTableStore } from '@/stores/useDagTableStore'

interface CellKeyboardOptions {
  rowId?: string
  colId: string
  isEditing: boolean
  isEditable: boolean
  computing?: boolean
  navigateTab?: (
    rowId: string,
    colId: string,
    direction: 'forward' | 'backward'
  ) => boolean | void
  navigateEnter?: (
    rowId: string,
    colId: string,
    direction: 'down' | 'up'
  ) => void
}

export function useDagCellKeyboard({
  rowId,
  colId,
  isEditing,
  isEditable,
  computing,
  navigateTab,
  navigateEnter,
}: CellKeyboardOptions) {
  const focusedCell = useDagTableStore(s => s.focusedCell)
  const focusRef = useRef<HTMLDivElement>(null)
  const isFocused = focusedCell?.rowId === rowId && focusedCell?.colId === colId

  useEffect(() => {
    if (isFocused && (!isEditing || computing)) focusRef.current?.focus()
  }, [isFocused, isEditing, computing])

  const handleCellClick = () => {
    if (rowId && !isEditing) {
      useDagTableStore.getState().setFocusedCell(rowId, colId)
    }
  }

  const handleDisplayKeyDown = (event: KeyboardEvent) => {
    if (!rowId || event.nativeEvent.isComposing) return
    const store = useDagTableStore.getState()
    if (event.key === 'Tab') {
      event.stopPropagation()
      if (
        navigateTab?.(rowId, colId, event.shiftKey ? 'backward' : 'forward') !==
        false
      ) {
        event.preventDefault()
      }
    } else if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      navigateEnter?.(rowId, colId, event.shiftKey ? 'up' : 'down')
    } else if (event.key === 'F2' && isEditable && !computing) {
      event.preventDefault()
      event.stopPropagation()
      store.setEditingCell(rowId, colId)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      store.clearFocus()
    }
  }

  return { focusRef, isFocused, handleCellClick, handleDisplayKeyDown }
}
